import { apiProblemText } from "../../api/api-error.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Finish setting up your business" — design §15.4.2's recovery path, a stage of the `Sign up / Log in` screen rather
 * than a screen of its own. AMENDED 2026-10-17 (owner decision; ADR 0071, verification-first sign-up): it is now how
 * every self-serve person sets up their first business.
 *
 * WHEN IT APPEARS. The person is signed in but belongs to no account: a log-in after a sign-up (which founds nothing
 * until the address is verified), or after a sign-up of before 2026-10-17 whose account step failed. The gateway says
 * so by raising `AuthSessionUnscopedError` with no choices, and `AuthScreen` renders this in place of the form.
 *
 * TWO STATES, read from the person's own onboarding draft (`GET /v1/me/onboarding-draft`):
 *   · the address is NOT verified — "Confirm your email" (the design's wording, first run's first step), with "Send a
 *     new link" and "I have confirmed it", which reads the draft again. `POST /v1/accounts` would refuse it
 *     `403 email_unverified`, so nothing is offered that cannot work;
 *   · the address IS verified — the business form, pre-filled with what the person typed at sign-up (the business
 *     name and country the API kept in the draft; the time zone proposed from the browser again, for confirmation).
 * It calls `createAccount` (`POST /v1/accounts`) and, on success, opens first run.
 *
 * THE IDEMPOTENCY KEY. `createAccount` requires one. A click on "Set up the business" after a failure is a RETRY of
 * the same intent if nothing on the form changed, so it reuses the key and cannot create a second business; if the
 * person changed anything, it is a new request and gets a new key (reusing the old one would be refused as `409`,
 * correctly). That is why the key lives here, next to the form contents it describes.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { isSupportedCountry } from "@wetalk/domain";
import { Button } from "@wetalk/ui";
import { useRef, useState, type FormEvent, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import type { OnboardingDraftView } from "../../api/slice/account.js";
import { ResendVerificationButton } from "../mail-landing/resend-verification.js";

import { useAdoptAuthSession } from "./adopt-auth-session.js";
import {
  BusinessFields,
  businessDraftProblem,
  type BusinessDraft,
} from "./business-fields.js";

export interface FinishSetupProps {
  /** What the person already typed on this screen, or an empty draft after a log-in. */
  readonly initial: BusinessDraft;
  /** Who is signed in, when the screen knows — shown so nobody sets up a business as someone else. */
  readonly emailAddress: string | null;
}

/** One submission's contents and the key that goes with them. */
interface Attempt {
  readonly fingerprint: string;
  readonly idempotencyKey: string;
}

/** The person's onboarding draft — the same key `/start/organization` reads, so one answer serves both. */
const ONBOARDING_DRAFT_KEY = ["onboarding-draft"] as const;

/** What the form opens with: the screen's own draft, or what sign-up kept in the server draft. */
export function initialDraftOf(initial: BusinessDraft, kept: OnboardingDraftView): BusinessDraft {
  if (initial.accountName.trim() !== "") return initial;
  const country = kept.input.country;
  return {
    accountName: kept.input.accountName ?? initial.accountName,
    country: country !== null && isSupportedCountry(country) ? country : initial.country,
    timeZone: initial.timeZone,
  };
}

export function FinishSetup({ initial, emailAddress }: FinishSetupProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const kept = useQuery({ queryKey: ONBOARDING_DRAFT_KEY, queryFn: () => gateway.onboardingDraft() });

  if (kept.isError) {
    return (
      <div className="c-entry-form">
        <h1 tabIndex={-1}>{portalText("core", "auth.finish_setting_up_your_business_db7e19")}</h1>
        <p className="wt-field-error" role="alert">
          {apiProblemText(kept.error, portalText("core", "auth.verify_first.draft_unreadable"))}
        </p>
        <Button variant="secondary" onClick={() => void kept.refetch()}>{portalText("core", "auth.verify_first.try_again")}</Button>
      </div>
    );
  }
  if (kept.data === undefined) {
    return <p className="c-note" role="status">{portalText("core", "auth.checking_820d60")}</p>;
  }
  const address = emailAddress ?? kept.data.emailAddress;
  if (!kept.data.emailVerified) {
    return <ConfirmEmailFirst emailAddress={address} rechecking={kept.isFetching} onRecheck={() => void kept.refetch()} />;
  }
  return <BusinessForm initial={initialDraftOf(initial, kept.data)} emailAddress={address} />;
}

/** "Confirm your email" — the business waits for the verified address (ADR 0071). */
function ConfirmEmailFirst(props: {
  readonly emailAddress: string;
  readonly rechecking: boolean;
  readonly onRecheck: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const [asked, setAsked] = useState(false);
  return (
    <div className="c-entry-form" aria-labelledby="finish-setup-confirm-title">
      <h1 id="finish-setup-confirm-title" tabIndex={-1}>{portalText("core", "auth.verify_first.title")}</h1>
      <p className="c-entry-lede">{portalText("core", "auth.verify_first.lede", { value0: props.emailAddress })}</p>
      {asked && !props.rechecking ? (
        <p className="c-note" role="status">{portalText("core", "auth.verify_first.still_unverified")}</p>
      ) : null}
      <Button
        variant="primary"
        block
        disabled={props.rechecking}
        onClick={() => {
          setAsked(true);
          props.onRecheck();
        }}
      >
        {props.rechecking ? portalText("core", "auth.checking_820d60") : portalText("core", "auth.verify_first.confirmed")}
      </Button>
      <ResendVerificationButton refresh={[ONBOARDING_DRAFT_KEY]} />
    </div>
  );
}

/** The business form, for a verified person. */
function BusinessForm({ initial, emailAddress }: { readonly initial: BusinessDraft; readonly emailAddress: string }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const navigate = useNavigate();
  const adoptAuthSession = useAdoptAuthSession();
  const [draft, setDraft] = useState<BusinessDraft>(initial);
  const [problem, setProblem] = usePortalMessage();
  const attempt = useRef<Attempt | null>(null);

  const create = useMutation({
    mutationFn: (request: Parameters<typeof gateway.createAccount>[0]) =>
      gateway.createAccount(request),
    onSuccess: async (opened) => {
      adoptAuthSession(opened);
      await navigate({ to: "/first-run" });
    },
    onError: (raised: Error) => {
      setProblem(() => (apiProblemText(raised, "")));
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setProblem(null);
    const refused = businessDraftProblem(draft);
    if (refused !== null || draft.country === null) {
      setProblem(() => (businessDraftProblem(draft) ?? portalText("core", "auth.choose_the_country_the_business_is_in_aa9bca")));
      return;
    }
    const request = {
      accountName: draft.accountName.trim(),
      country: draft.country,
      timeZone: draft.timeZone,
    };
    const fingerprint = JSON.stringify(request);
    if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, idempotencyKey: globalThis.crypto.randomUUID() };
    }
    create.mutate({ ...request, idempotencyKey: attempt.current.idempotencyKey });
  };

  return (
    <form className="c-entry-form" onSubmit={onSubmit} aria-labelledby="finish-setup-title">
      <h1 id="finish-setup-title" tabIndex={-1}>{portalText("core", "auth.finish_setting_up_your_business_db7e19")}</h1>
      <p className="c-entry-lede">{portalText("core", "auth.verify_first.ready_lede", { value0: emailAddress })}</p>

      <BusinessFields draft={draft} onChange={setDraft} />

      {problem === null ? null : (
        <p className="wt-field-error" role="alert">
          {problem}
        </p>
      )}

      <Button variant="primary" type="submit" block disabled={create.isPending}>
        {create.isPending ? portalText("core", "auth.setting_it_up_a482e0") : portalText("core", "auth.set_up_the_business_2bc431")}
      </Button>
    </form>
  );
}
